import { nms } from '../utils/nms.js';
import cfg from '../../config.json';

const STRIDES  = [8, 16, 32];
const VARIANCE = [0.1, 0.2];

/**
 * Decode YuNet ONNX outputs — 12-tensor per-stride format.
 *
 * Output layout (4 groups × 3 strides, one entry per stride in [8,16,32]):
 *   indices [0,1,2]   cls  [1, N, 1]  — face classification score
 *   indices [3,4,5]   iou  [1, N, 1]  — IoU quality score
 *   indices [6,7,8]   box  [1, N, 4]  — box offsets (cx, cy, w, h)
 *   indices [9,10,11] lm   [1, N, 10] — landmarks (unused)
 *
 * 1 anchor per feature-map location, anchor size = stride.
 * Final score = sqrt(sigmoid(cls) × sigmoid(iou)).
 */
export function decodeOutputs(outputs, inputW, inputH, threshold) {
  const tensors = Object.values(outputs);

  if (tensors.length !== 12) {
    console.warn('[yunet] expected 12 output tensors, got', tensors.length,
      tensors.map(t => Array.from(t.dims)));
    return [];
  }

  const boxes = [];

  for (let s = 0; s < STRIDES.length; s++) {
    const stride = STRIDES[s];
    const cls    = tensors[s].data;         // [N]
    const iou    = tensors[s + 3].data;     // [N]
    const box    = tensors[s + 6].data;     // [N * 4]

    const rows = Math.ceil(inputH / stride);
    const cols = Math.ceil(inputW / stride);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;

        const score = Math.sqrt(cls[i] * iou[i]);
        if (score < threshold) continue;

        const px = (c + 0.5) * stride;
        const cy_prior = (r + 0.5) * stride;

        const cx = px          + box[i * 4]     * VARIANCE[0] * stride;
        const cy = cy_prior    + box[i * 4 + 1] * VARIANCE[0] * stride;
        const bw = stride * Math.exp(box[i * 4 + 2] * VARIANCE[1]);
        const bh = stride * Math.exp(box[i * 4 + 3] * VARIANCE[1]);

        boxes.push({
          x1:    Math.max(0,      cx - bw / 2),
          y1:    Math.max(0,      cy - bh / 2),
          x2:    Math.min(inputW, cx + bw / 2),
          y2:    Math.min(inputH, cy + bh / 2),
          score,
        });
      }
    }
  }

  return nms(boxes, cfg.detection.nmsIou);
}

